import type { MutableRefObject } from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';

export type onDirtyCheckType = (checker: () => boolean) => void;

export type OnCloseOptions = { cancelled?: boolean };

type ClosePhase = 'idle' | 'settling' | 'ready';

interface UseUnsavedChangesModalProps<T> {
    savedData: T;
    onClose: (options?: OnCloseOptions) => void;
}

interface UseUnsavedChangesModalReturn {
    isDirtyRef: MutableRefObject<boolean>;
    onDirtyCheck: onDirtyCheckType;
    showConfirmationModal: boolean;
    handleClose: () => void;
    handleCancelChanges: () => void;
    handleContinueEditing: () => void;
}

/**
 * Hook to manage unsaved changes confirmation modal behavior
 *
 * On close, it blurs the active element to flush pending field dispatches (e.g., amount fields
 * that update Redux only on blur), then uses a two-phase deferred check ('settling' → 'ready')
 * to allow child effects to sync local state with Redux before reading isDirtyRef.
 *
 * If the form is dirty after settling, shows a confirmation modal; otherwise closes directly.
 *
 * @example
 * ```tsx
 * const accessories = useSelector(accessoriesSelectors.getAccessoriesList);
 * const {
 *   isDirtyRef,
 *   showConfirmationModal,
 *   handleClose,
 *   handleCancelChanges,
 *   handleContinueEditing
 * } = useUnsavedChangesModal({
 *   savedData: accessories,
 *   onClose: () => dispatch(closeSlideout())
 * });
 *
 * return (
 *   <>
 *     <SlideOutWrapper onClose={handleClose}>
 *       <MyForm isDirtyRef={isDirtyRef} />
 *     </SlideOutWrapper>
 *     <UnsavedChangesConfirmationModal
 *       show={showConfirmationModal}
 *       onCancelChanges={handleCancelChanges}
 *       onContinueEditing={handleContinueEditing}
 *     />
 *   </>
 * );
 * ```
 */
export const useUnsavedChangesModal = <T>({ savedData, onClose }: UseUnsavedChangesModalProps<T>): UseUnsavedChangesModalReturn => {
    const isDirtyRef = useRef(false);
    const dirtyValidatorRef = useRef<() => boolean>();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [closePhase, setClosePhase] = useState<ClosePhase>('idle');

    useEffect(() => {
        isDirtyRef.current = dirtyValidatorRef.current?.() || false;
    }, [savedData]);

    useEffect(() => {
        if (closePhase === 'idle') return;
        if (closePhase === 'settling') {
            setClosePhase('ready');
            return;
        }
        setClosePhase('idle');
        if (isDirtyRef.current) {
            setShowConfirmationModal(true);
        } else {
            onClose();
        }
    }, [closePhase, onClose]);

    const handleClose = useCallback(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setClosePhase('settling');
    }, []);

    const handleCancelChanges = useCallback(() => {
        setShowConfirmationModal(false);
        isDirtyRef.current = false;
        onClose({ cancelled: true });
    }, [onClose]);

    const handleContinueEditing = useCallback(() => {
        setShowConfirmationModal(false);
        setClosePhase('idle');
    }, []);

    const onDirtyCheck = useCallback((checker: () => boolean) => {
        isDirtyRef.current = checker();
        dirtyValidatorRef.current = checker;
    }, []);

    return {
        isDirtyRef,
        onDirtyCheck,
        showConfirmationModal,
        handleClose,
        handleCancelChanges,
        handleContinueEditing
    };
};
